/**
 * @fileoverview Componente responsável por atualizar o favicon com o indicador de mensagens não lidas.
 */

import { useContext, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";
import { isInternalOnly } from "../../utils/accessScope";
import alertSound from "../../assets/sound.mp3";
import { i18n } from "../../translate/i18n";

// Atualiza somente o título da página com contador (estilo WhatsApp)
const updateCounterTitle = (count, baseTitle) => {
  const base = baseTitle || document.title.replace(/^\(\d+\)\s*/, "");
  document.title = count > 0 ? `(${count}) ${base}` : base;
};

const getNotificationVolume = () => {
  const value = parseFloat(localStorage.getItem("volume"));
  if (Number.isNaN(value)) return 1;
  return Math.max(0, Math.min(1, value));
};

const isDesktopNotificationSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

const getCurrentTicketRouteId = () => {
  if (typeof window === "undefined") return null;

  const pathParts = window.location.pathname.split("/").filter(Boolean);
  if (pathParts[0] !== "tickets" || !pathParts[1]) return null;

  return pathParts[1];
};

const isTicketsScreenVisible = () => {
  if (typeof window === "undefined") return false;

  const isTicketsRoute = window.location.pathname.startsWith("/tickets");
  return isTicketsRoute && document.visibilityState === "visible";
};

const getMessagePreview = (message = {}) => {
  const body = String(message.body || "").trim();
  if (body) return body;

  const mediaType = String(message.mediaType || "").toLowerCase();

  if (mediaType.includes("image")) return "Nova imagem";
  if (mediaType.includes("video")) return "Novo vídeo";
  if (mediaType.includes("audio") || mediaType.includes("ptt")) return "Novo áudio";
  if (mediaType.includes("document") || mediaType.includes("application")) return "Novo documento";
  if (mediaType.includes("sticker")) return "Novo sticker";
  if (mediaType.includes("location")) return "Nova localização";

  return "Nova mensagem";
};

// Mantém um contador de conversas não lidas considerando as filas do usuário
const FaviconUnreadBadge = () => {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const isAuth = auth?.isAuth;
  const socketManager = useContext(SocketContext);
  // O contador e os avisos são das conversas: quem não as vê ("Apenas Interno")
  // não pede a lista (a API recusava com 403 a cada carga da página). Espera o
  // acesso da pessoa chegar, para não pedir antes com a regra de hoje.
  const { can, loaded } = usePermission();
  const seesTickets = loaded && can("tickets.view", !isInternalOnly(user));

  const [unreadCount, setUnreadCount] = useState(0);
  const [webNotificationsEnabled, setWebNotificationsEnabled] = useState(false);
  const baseTitleRef = useRef(null);
  const soundRef = useRef(null);
  const webNotificationsEnabledRef = useRef(false);
  const ticketsRef = useRef(new Map()); // ticketId -> ticket
  const desktopNotificationsRef = useRef(new Map()); // ticketId -> Notification

  useEffect(() => {
    soundRef.current = new Audio(alertSound);
    soundRef.current.preload = "auto";

    return () => {
      if (soundRef.current) {
        soundRef.current.pause();
        soundRef.current.src = "";
      }
    };
  }, []);

  useEffect(() => {
    // Captura o título original da página uma única vez
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title.replace(/^\(\d+\)\s*/, "");
    }
  }, []);

  useEffect(() => {
    webNotificationsEnabledRef.current = webNotificationsEnabled;
  }, [webNotificationsEnabled]);

  useEffect(() => {
    if (!webNotificationsEnabled || !isDesktopNotificationSupported()) return;
    if (Notification.permission !== "default") return;

    Notification.requestPermission().catch(() => {});
  }, [webNotificationsEnabled]);

  useEffect(() => {
    if (!isAuth || !user || !seesTickets) return; // aguarda auth carregar
    const companyId = localStorage.getItem("companyId") || `${user.companyId || ""}`;
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);
    let isMounted = true;

    const userId = user?.id;
    const userProfile = String(user?.profile || "").toLowerCase();
    const isAdminProfile = userProfile === "admin" || userProfile === "superadmin";
    const userQueues = (user?.queues || []).map((q) => q.id);

    const visibleForUser = (ticket) => {
      if (!ticket) return false;

      if (isAdminProfile) {
        return ticket.status !== "closed";
      }

      // Regras: se tem user vinculado diferente, não conta; se tem fila fora da lista do usuário, não conta
      if (ticket.userId && ticket.userId !== userId) return false;
      if (ticket.queueId && userQueues.indexOf(ticket.queueId) === -1) return false;
      // Não contar tickets fechados
      if (ticket.status === "closed") return false;
      return true;
    };

    const recompute = () => {
      let total = 0;
      ticketsRef.current.forEach((t) => {
        if (visibleForUser(t) && Number(t.unreadMessages) > 0) total += 1;
      });
      setUnreadCount(total);
    };

    const playNotificationSound = () => {
      const audio = soundRef.current;
      if (!audio) return;

      const volume = getNotificationVolume();
      if (volume <= 0) return;

      audio.volume = volume;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    };

    const closeDesktopNotification = (ticketKey) => {
      const key = String(ticketKey || "");
      if (!key) return;

      const activeNotification = desktopNotificationsRef.current.get(key);
      if (activeNotification) {
        activeNotification.close();
        desktopNotificationsRef.current.delete(key);
      }
    };

    const closeAllDesktopNotifications = () => {
      desktopNotificationsRef.current.forEach((notification) => {
        notification.close();
      });
      desktopNotificationsRef.current.clear();
    };

    const fetchWebNotificationsSetting = () => {
      api
        .get("/settings")
        .then(({ data }) => {
          if (!isMounted || !Array.isArray(data)) return;

          const webNotificationsSetting = data.find(
            (setting) => setting.key === "webNotifications"
          );

          setWebNotificationsEnabled(
            webNotificationsSetting?.value === "enabled"
          );
        })
        .catch(() => {
          if (isMounted) {
            setWebNotificationsEnabled(false);
          }
        });
    };

    const showDesktopNotification = (data) => {
      if (!webNotificationsEnabledRef.current) return;
      if (!isDesktopNotificationSupported()) return;
      if (Notification.permission !== "granted") return;

      const ticket = data?.ticket;
      if (!ticket) return;

      const currentTicketRouteId = getCurrentTicketRouteId();
      const isViewingSameTicket =
        document.visibilityState === "visible" &&
        currentTicketRouteId &&
        (currentTicketRouteId === String(ticket.id) ||
          currentTicketRouteId === String(ticket.uuid));

      if (isViewingSameTicket) return;

      const ticketKey = String(ticket.id || ticket.uuid || "");
      if (!ticketKey) return;

      closeDesktopNotification(ticketKey);

      const contact = data.contact || ticket.contact || {};
      const titlePrefix = i18n.t("tickets.notification.message");
      const title = `${titlePrefix} ${contact.name || ""}`.trim();
      const currentHour = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      const notification = new Notification(title || "Nova mensagem", {
        body: `${getMessagePreview(data.message)} - ${currentHour}`,
        icon: contact.profilePicUrl || undefined,
        tag: ticketKey,
        renotify: true,
      });

      notification.onclose = () => {
        desktopNotificationsRef.current.delete(ticketKey);
      };

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();

        const ticketPath = ticket.uuid ? `/tickets/${ticket.uuid}` : "/tickets";

        try {
          window.history.pushState({}, "", ticketPath);
          window.dispatchEvent(new PopStateEvent("popstate"));
        } catch (error) {
          window.location.assign(ticketPath);
        }

        notification.close();
      };

      desktopNotificationsRef.current.set(ticketKey, notification);
    };

    const handleReady = () => {
      socket.emit("joinNotification");
      // Bootstrap inicial: pedir lista de tickets não lidos do usuário
      api
        .get("/tickets", {
          params: {
            withUnreadMessages: true,
            pageNumber: 1,
            queueIds: JSON.stringify(userQueues),
          },
        })
        .then(({ data }) => {
          if (Array.isArray(data?.tickets)) {
            data.tickets.forEach((t) => ticketsRef.current.set(t.id, t));
            const visible = data.tickets.filter((t) => visibleForUser(t));
            if (isMounted) {
              setUnreadCount(
                visible.filter((t) => Number(t.unreadMessages) > 0).length
              );
            }
          }
        })
        .catch(() => {});
    };

    const handleTicketUpdate = (data) => {
      if (data.action === "update" && data.ticket) {
        ticketsRef.current.set(data.ticket.id, data.ticket);
        recompute();
      }
      if (data.action === "delete") {
        ticketsRef.current.delete(data.ticketId);
        closeDesktopNotification(data.ticketId);
        recompute();
      }
      if (data.action === "updateUnread") {
        const t = ticketsRef.current.get(data.ticketId);
        closeDesktopNotification(data.ticketId);
        if (t) {
          t.unreadMessages = 0;
          ticketsRef.current.set(t.id, t);
          recompute();
        }
      }
      if (
        data.action === "update" &&
        data.ticket &&
        Number(data.ticket.unreadMessages) === 0
      ) {
        closeDesktopNotification(data.ticket.id);
      }
    };

    const handleSettingUpdate = (data) => {
      if (data?.action !== "update" || !data.setting) return;
      if (data.setting.key !== "webNotifications") return;

      setWebNotificationsEnabled(data.setting.value === "enabled");
    };

    const handleAppMessage = (data) => {
      if (data.action === "create" && data.ticket) {
        ticketsRef.current.set(data.ticket.id, data.ticket);
        recompute();

        const isIncomingCustomerMessage =
          data.message &&
          !data.message.fromMe &&
          !data.ticket.isGroup;

        const shouldNotify = isIncomingCustomerMessage && visibleForUser(data.ticket);

        if (shouldNotify && !isTicketsScreenVisible()) {
          playNotificationSound();
          showDesktopNotification(data);
        }
      }
    };

    fetchWebNotificationsSetting();

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-ticket`, handleTicketUpdate);
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-settings`, handleSettingUpdate);

    return () => {
      isMounted = false;

      socket.off("ready", handleReady);
      socket.off(`company-${companyId}-ticket`, handleTicketUpdate);
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      socket.off(`company-${companyId}-settings`, handleSettingUpdate);
      closeAllDesktopNotifications();
    };
  }, [socketManager, user, isAuth, seesTickets]);

  useEffect(() => {
    updateCounterTitle(unreadCount);
  }, [unreadCount]);

  return null;
};

export default FaviconUnreadBadge;
